import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Progress } from "@/components/ui/progress"
import { Slider } from "@/components/ui/slider"
import { AnimatedCard } from "./AnimatedCard"
import { GlassCard } from "./GlassCard"
import { ProgressRing } from "./ProgressRing"
import { useApexState } from "@/hooks/useApexState"
import { 
  Crown, 
  Sparkles,
  Brain,
  Target,
  Palette,
  Activity,
  Clock,
  MapPin,
  User,
  Camera,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Circle,
  Zap,
  Heart,
  Star
} from "lucide-react"

interface OnboardingStep {
  id: string
  title: string
  subtitle: string
  type: 'welcome' | 'profile' | 'preferences' | 'goals' | 'personality' | 'recommendations' | 'complete'
  required: boolean
  progress: number
}

interface PersonalityProfile {
  aesthetic: { minimalist: number; bold: number; architectural: number; organic: number }
  movement: { high_intensity: number; mindful: number; social: number; solo: number }
  lifestyle: { morning: number; evening: number; structured: number; flexible: number }
  values: { sustainability: number; exclusivity: number; community: number; innovation: number }
}

interface UserGoals {
  primary: string[]
  movement_frequency: number
  style_evolution: string
  community_engagement: string
  tier_aspiration: 'creator' | 'architect'
}

interface AdvancedOnboardingProps {
  isOpen: boolean
  onComplete: (profile: any) => void
}

const onboardingSteps: OnboardingStep[] = [
  { id: 'welcome', title: 'Welcome to Apex', subtitle: 'Your exclusive journey begins', type: 'welcome', required: true, progress: 0 },
  { id: 'profile', title: 'Build Your Profile', subtitle: 'Tell us about yourself', type: 'profile', required: true, progress: 15 },
  { id: 'preferences', title: 'Style Preferences', subtitle: 'Define your aesthetic', type: 'preferences', required: true, progress: 30 },
  { id: 'goals', title: 'Set Your Goals', subtitle: 'What drives you?', type: 'goals', required: true, progress: 50 },
  { id: 'personality', title: 'Personality Mapping', subtitle: 'AI-powered profiling', type: 'personality', required: false, progress: 70 },
  { id: 'recommendations', title: 'Your Curated Experience', subtitle: 'AI-generated recommendations', type: 'recommendations', required: false, progress: 90 },
  { id: 'complete', title: 'Ready to Shape', subtitle: 'Your world awaits', type: 'complete', required: true, progress: 100 }
]

const styleOptions = [
  { id: 'minimalist', label: 'Minimalist', description: 'Clean lines, essential forms', image: 'https://images.unsplash.com/photo-1583900985737-6d0495555783?w=150&h=150&fit=crop' },
  { id: 'architectural', label: 'Architectural', description: 'Structured, geometric', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=150&h=150&fit=crop' },
  { id: 'organic', label: 'Organic', description: 'Natural, flowing forms', image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=150&h=150&fit=crop' },
  { id: 'bold', label: 'Bold', description: 'Statement pieces, contrast', image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=150&h=150&fit=crop' }
]

const movementOptions = [
  { id: 'mindful', label: 'Mindful Movement', description: 'Yoga, stretching, meditation' },
  { id: 'high-intensity', label: 'High Intensity', description: 'HIIT, running, cycling' },
  { id: 'social', label: 'Social Fitness', description: 'Group classes, team sports' },
  { id: 'solo', label: 'Solo Training', description: 'Individual workouts, personal time' }
]

const goalOptions = [
  'Build confidence through style',
  'Connect with like-minded community',
  'Develop personal aesthetic',
  'Achieve movement consistency',
  'Express creativity through fashion',
  'Earn exclusive tier benefits',
  'Document style evolution',
  'Inspire others through content'
]

export function AdvancedOnboarding({ isOpen, onComplete }: AdvancedOnboardingProps) {
  const { actions } = useApexState()
  const [currentStep, setCurrentStep] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  
  // User data collection
  const [userProfile, setUserProfile] = useState({
    name: '',
    location: '',
    bio: '',
    interests: [] as string[]
  })
  
  const [selectedStyles, setSelectedStyles] = useState<string[]>([])
  const [selectedMovement, setSelectedMovement] = useState<string[]>([])
  const [selectedGoals, setSelectedGoals] = useState<string[]>([])
  
  const [personalityProfile, setPersonalityProfile] = useState<PersonalityProfile>({
    aesthetic: { minimalist: 50, bold: 50, architectural: 50, organic: 50 },
    movement: { high_intensity: 50, mindful: 50, social: 50, solo: 50 },
    lifestyle: { morning: 50, evening: 50, structured: 50, flexible: 50 },
    values: { sustainability: 50, exclusivity: 50, community: 50, innovation: 50 }
  })
  
  const [goals, setGoals] = useState<UserGoals>({
    primary: [],
    movement_frequency: 3,
    style_evolution: 'gradual',
    community_engagement: 'moderate',
    tier_aspiration: 'creator'
  })

  const [aiRecommendations, setAiRecommendations] = useState<any[]>([])

  const currentStepData = onboardingSteps[currentStep]

  const nextStep = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentStep(currentStep + 1)
        setIsTransitioning(false)
      }, 300)
    }
  }

  const previousStep = () => {
    if (currentStep > 0) {
      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentStep(currentStep - 1)
        setIsTransitioning(false)
      }, 300)
    }
  }

  const canProceed = () => {
    switch (currentStepData.type) {
      case 'profile':
        return userProfile.name.trim().length > 0
      case 'preferences':
        return selectedStyles.length > 0 && selectedMovement.length > 0
      case 'goals':
        return selectedGoals.length > 0
      default:
        return true
    }
  }

  const generateAiRecommendations = () => {
    // Simulate AI processing based on collected data
    const recommendations = [
      {
        type: 'product',
        title: 'Recommended for You: Kinetic Joggers',
        description: 'Based on your mindful movement preference and architectural aesthetic',
        confidence: 94
      },
      {
        type: 'community',
        title: 'Connect with Maya Rodriguez',
        description: 'Architect member with similar minimalist aesthetic',
        confidence: 89
      },
      {
        type: 'challenge',
        title: 'Perfect First Challenge: Form & Function',
        description: 'Matches your style evolution goals and movement frequency',
        confidence: 92
      }
    ]
    setAiRecommendations(recommendations)
  }

  useEffect(() => {
    if (currentStepData.type === 'recommendations') {
      generateAiRecommendations()
    }
  }, [currentStep])

  const handleComplete = () => {
    const completeProfile = {
      profile: userProfile,
      styles: selectedStyles,
      movement: selectedMovement,
      goals: selectedGoals,
      personality: personalityProfile,
      userGoals: goals,
      completedAt: new Date().toISOString()
    }
    
    onComplete(completeProfile)
  }

  const renderStepContent = () => {
    switch (currentStepData.type) {
      case 'welcome':
        return (
          <div className="text-center space-y-6">
            <div className="w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-6">
              <Crown size={40} className="text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-4">Welcome to Gameshape Apex</h2>
              <p className="text-lg text-muted-foreground max-w-md mx-auto leading-relaxed">
                An exclusive community where style meets movement. Let's create your personalized experience.
              </p>
            </div>
            <div className="flex items-center justify-center gap-4 pt-4">
              <Badge className="bg-primary/20 text-primary">Exclusive Access</Badge>
              <Badge className="bg-accent/20 text-accent">AI Personalization</Badge>
              <Badge className="bg-secondary/20 text-secondary">Community Driven</Badge>
            </div>
          </div>
        )

      case 'profile':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">Tell Us About You</h2>
              <p className="text-muted-foreground">Help us personalize your Apex experience</p>
            </div>
            
            <div className="space-y-4 max-w-md mx-auto">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Name *</label>
                <Input
                  placeholder="Your name"
                  value={userProfile.name}
                  onChange={(e) => setUserProfile(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-input/50 border-white/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Location</label>
                <Input
                  placeholder="City, Country"
                  value={userProfile.location}
                  onChange={(e) => setUserProfile(prev => ({ ...prev, location: e.target.value }))}
                  className="bg-input/50 border-white/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">About You</label>
                <Textarea
                  placeholder="Share what drives your style and movement..."
                  value={userProfile.bio}
                  onChange={(e) => setUserProfile(prev => ({ ...prev, bio: e.target.value }))}
                  className="bg-input/50 border-white/20 min-h-[100px]"
                />
              </div>
            </div>
          </div>
        )

      case 'preferences':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">Define Your Aesthetic</h2>
              <p className="text-muted-foreground">Choose styles and movements that resonate with you</p>
            </div>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Palette size={20} />
                  Style Preferences
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {styleOptions.map(style => (
                    <AnimatedCard key={style.id} hoverScale>
                      <button
                        onClick={() => {
                          setSelectedStyles(prev => 
                            prev.includes(style.id) 
                              ? prev.filter(s => s !== style.id)
                              : [...prev, style.id]
                          )
                        }}
                        className={`w-full p-4 rounded-lg border-2 transition-all ${
                          selectedStyles.includes(style.id)
                            ? 'border-primary bg-primary/10'
                            : 'border-white/20 hover:border-white/40'
                        }`}
                      >
                        <div className="aspect-square rounded-lg overflow-hidden mb-3">
                          <img
                            src={style.image}
                            alt={style.label}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h4 className="font-medium text-foreground mb-1">{style.label}</h4>
                        <p className="text-xs text-muted-foreground">{style.description}</p>
                        {selectedStyles.includes(style.id) && (
                          <CheckCircle size={16} className="text-primary mx-auto mt-2" />
                        )}
                      </button>
                    </AnimatedCard>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Activity size={20} />
                  Movement Style
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {movementOptions.map(movement => (
                    <button
                      key={movement.id}
                      onClick={() => {
                        setSelectedMovement(prev => 
                          prev.includes(movement.id)
                            ? prev.filter(m => m !== movement.id)
                            : [...prev, movement.id]
                        )
                      }}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedMovement.includes(movement.id)
                          ? 'border-accent bg-accent/10'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-foreground">{movement.label}</h4>
                          <p className="text-sm text-muted-foreground">{movement.description}</p>
                        </div>
                        {selectedMovement.includes(movement.id) ? (
                          <CheckCircle size={20} className="text-accent" />
                        ) : (
                          <Circle size={20} className="text-muted-foreground" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )

      case 'goals':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">What Drives You?</h2>
              <p className="text-muted-foreground">Select your primary goals and aspirations</p>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Target size={20} />
                  Primary Goals
                </h3>
                <div className="grid gap-3">
                  {goalOptions.map(goal => (
                    <button
                      key={goal}
                      onClick={() => {
                        setSelectedGoals(prev => 
                          prev.includes(goal)
                            ? prev.filter(g => g !== goal)
                            : [...prev, goal]
                        )
                      }}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedGoals.includes(goal)
                          ? 'border-primary bg-primary/10'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-foreground">{goal}</span>
                        {selectedGoals.includes(goal) ? (
                          <CheckCircle size={20} className="text-primary" />
                        ) : (
                          <Circle size={20} className="text-muted-foreground" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Movement Frequency: {goals.movement_frequency}x/week
                  </label>
                  <Slider
                    value={[goals.movement_frequency]}
                    onValueChange={([value]) => setGoals(prev => ({ ...prev, movement_frequency: value }))}
                    min={1}
                    max={7}
                    step={1}
                    className="mb-2"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Tier Aspiration</label>
                  <select
                    value={goals.tier_aspiration}
                    onChange={(e) => setGoals(prev => ({ ...prev, tier_aspiration: e.target.value as any }))}
                    className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground"
                  >
                    <option value="creator">Creator</option>
                    <option value="architect">Architect</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )

      case 'personality':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
                <Brain size={28} className="text-primary" />
                Personality Mapping
              </h2>
              <p className="text-muted-foreground">Help our AI understand your unique style DNA</p>
              <Badge className="bg-purple-500/20 text-purple-400 text-xs mt-2">AI Powered</Badge>
            </div>
            
            <div className="space-y-6 max-w-2xl mx-auto">
              {Object.entries(personalityProfile).map(([category, values]) => (
                <div key={category} className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground capitalize">
                    {category} Preferences
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(values).map(([trait, value]) => (
                      <div key={trait}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm text-foreground capitalize">
                            {trait.replace('_', ' ')}
                          </label>
                          <span className="text-sm text-muted-foreground">{value}%</span>
                        </div>
                        <Slider
                          value={[value]}
                          onValueChange={([newValue]) => 
                            setPersonalityProfile(prev => ({
                              ...prev,
                              [category]: { ...prev[category], [trait]: newValue }
                            }))
                          }
                          min={0}
                          max={100}
                          step={5}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )

      case 'recommendations':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
                <Sparkles size={28} className="text-primary" />
                Your Curated Experience
              </h2>
              <p className="text-muted-foreground">AI-generated recommendations based on your profile</p>
            </div>
            
            <div className="space-y-4 max-w-2xl mx-auto">
              {aiRecommendations.map((rec, index) => (
                <AnimatedCard key={index}>
                  <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground mb-2">{rec.title}</h4>
                          <p className="text-sm text-muted-foreground mb-3">{rec.description}</p>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-primary/20 text-primary text-xs">
                              {rec.type}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {rec.confidence}% match
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <ProgressRing progress={rec.confidence} size="sm" color="primary">
                            <span className="text-xs font-bold">{rec.confidence}%</span>
                          </ProgressRing>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </AnimatedCard>
              ))}
            </div>
          </div>
        )

      case 'complete':
        return (
          <div className="text-center space-y-6">
            <div className="w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br from-accent to-primary flex items-center justify-center mb-6">
              <Star size={40} className="text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-4">Welcome to Your Apex Journey</h2>
              <p className="text-lg text-muted-foreground max-w-md mx-auto leading-relaxed">
                Your personalized experience is ready. Time to shape your world.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto pt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{selectedStyles.length}</div>
                <div className="text-xs text-muted-foreground">Style Preferences</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent">{selectedGoals.length}</div>
                <div className="text-xs text-muted-foreground">Personal Goals</div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-background">
      <div className="min-h-screen flex flex-col">
        {/* Progress Header */}
        <div className="border-b border-border bg-card/50 backdrop-blur-lg">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Crown size={16} className="text-primary" />
                </div>
                <div>
                  <h1 className="font-semibold text-foreground">Apex Onboarding</h1>
                  <p className="text-xs text-muted-foreground">
                    Step {currentStep + 1} of {onboardingSteps.length}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {Math.round(currentStepData.progress)}%
                </span>
                <ProgressRing
                  progress={currentStepData.progress}
                  size="sm"
                  color="primary"
                >
                  <span className="text-xs font-bold">{Math.round(currentStepData.progress)}%</span>
                </ProgressRing>
              </div>
            </div>
            
            <Progress value={currentStepData.progress} className="h-1" />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-12">
            <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              {renderStepContent()}
            </div>
          </div>
        </div>

        {/* Navigation Footer */}
        <div className="border-t border-border bg-card/50 backdrop-blur-lg">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={previousStep}
                disabled={currentStep === 0}
                className="flex items-center gap-2"
              >
                <ChevronLeft size={16} />
                Previous
              </Button>

              <div className="flex gap-1">
                {onboardingSteps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentStep
                        ? 'bg-primary'
                        : index < currentStep
                        ? 'bg-accent'
                        : 'bg-muted'
                    }`}
                  />
                ))}
              </div>

              {currentStep === onboardingSteps.length - 1 ? (
                <Button
                  onClick={handleComplete}
                  className="bg-primary hover:bg-primary/90 text-black flex items-center gap-2"
                >
                  Complete Setup
                  <Zap size={16} />
                </Button>
              ) : (
                <Button
                  onClick={nextStep}
                  disabled={!canProceed()}
                  className="flex items-center gap-2"
                >
                  Next
                  <ChevronRight size={16} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}